/* eslint-disable react/require-default-props */
import {
  ContractStatus,
  convertContractStatus,
  ScopeTermKind,
  TermKind,
  TermState,
} from '@/api/theHive/notices/enumerations';
import {
  AdministrativeTermData,
  SignedContractData,
} from '@/api/theHive/notices/types';
import {
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { useState } from 'react';

import { convertDocumentStatus } from '../../../../../../api/enumerations';
import { Constants } from '../../../../../../constants/hiveNotices';
import {
  IconAttachFileMS,
  IconDescriptionMS,
} from '../../../../../../constants/icons';
import { formatOnlyDate } from '../../../../../../helpers';
import useGeneral from '../../../../../../hooks/useGeneral';
import {
  TableCellHeader,
  StyledCell,
  StyledRow,
  StatusCell,
  DownloadButton,
  ActionButton,
  DocumentNameButton,
  StyledTooltip,
  ButtonsWrapper,
  StyledTable,
} from '../styles';
import {
  getDocumentStatusStyle,
  isContractPendingReview,
  stripHtml,
} from '../utils';
import { ApproveAdministrativeDialog } from './ApproveAdministrativeDialog';
import { RejectDialog } from './RejectDialog';
import { RejectReasonDialog } from './RejectionReason';

interface AdministrativeDocumentsTableProps {
  name: string;
  data: SignedContractData | null;
  terms: AdministrativeTermData[];
  docLoading: boolean;
  handleApprove: (docId?: number) => Promise<void>;
  handleReject: (reason: string, docId?: number) => Promise<void>;
  disabled?: boolean;
}

interface AdministrativeRow {
  key: string;
  fileName: string;
  label: string;
  url: string | null;
  sentAt: string | null;
  statusLabel: string;
  statusColor: string;
  rejectionReason: string | null;
  actionable: boolean;
  approveWarning: string;
  docId?: number;
}

const contractStatusColor = (status: ContractStatus): string => {
  if (status === ContractStatus.APPROVED) return '#1E8A4C';
  if (status === ContractStatus.REJECTED) return '#F90B0B';
  return '#FF6600';
};

const isScopeTerm = (term: AdministrativeTermData): boolean =>
  term.kind === TermKind.SCOPE_ADDITION || term.kind === TermKind.SCOPE_REMOVAL;

const collaboratorTermLabel = (term: AdministrativeTermData): string =>
  term.kind === TermKind.DISCHARGE
    ? Constants.dischargeTermLabel
    : Constants.additionTermLabel;

const termLabel = (term: AdministrativeTermData): string => {
  const kind = isScopeTerm(term)
    ? term.title || Constants.scopeTermLabel
    : collaboratorTermLabel(term);

  return term.owner_name
    ? `${kind} ${Constants.administrativeDocumentOwner.replace(
        '{owner}',
        term.owner_name
      )}`
    : kind;
};

const scopeTermApproveWarning = (term: AdministrativeTermData): string => {
  const warning =
    term.kind === TermKind.SCOPE_REMOVAL
      ? Constants.approveScopeRemovalWarning
      : Constants.approveScopeAdditionWarning;

  return warning.replace(
    '{items}',
    term.scope_kind === ScopeTermKind.TYPOLOGY_GROUPS
      ? Constants.scopeTermTypologyGroups
      : Constants.scopeTermPoles
  );
};

const termApproveWarning = (term: AdministrativeTermData): string => {
  if (isScopeTerm(term)) return scopeTermApproveWarning(term);

  if (term.kind === TermKind.ADDITION)
    return Constants.approveAdditionTermWarning;

  return term.is_company_discharge
    ? Constants.approveCompanyDischargeWarning
    : Constants.approveDischargeTermWarning;
};

export default function AdministrativeDocumentsTable({
  name,
  data,
  terms,
  docLoading,
  handleApprove,
  handleReject,
  disabled = false,
}: AdministrativeDocumentsTableProps): JSX.Element {
  const [openRejectionDialog, setOpenRejectionDialog] =
    useState<AdministrativeRow>();

  const { handleDownloadFile } = useGeneral();

  const contractRow = (contract: SignedContractData): AdministrativeRow => ({
    key: 'signed-contract',
    fileName:
      contract.display_name ||
      contract.original_filename ||
      Constants.contractSection,
    label: Constants.contractSection,
    url: contract.url,
    sentAt: contract.uploaded_at,
    statusLabel: contract.status ? convertContractStatus(contract.status) : '',
    statusColor: contract.status ? contractStatusColor(contract.status) : '',
    rejectionReason: contract.rejection_reason,
    actionable: !disabled && isContractPendingReview(contract),
    approveWarning: Constants.approveContractWarning,
  });

  const termRow = (term: AdministrativeTermData): AdministrativeRow => ({
    key: `term-${term.document.id}`,
    fileName:
      term.document.display_name || term.document.original_filename || '',
    label: termLabel(term),
    url: term.document.url,
    sentAt: term.document.sent_at,
    statusLabel: convertDocumentStatus(term.document.status),
    statusColor: getDocumentStatusStyle(term.document.status).color,
    rejectionReason: term.document.rejection_reason,
    actionable: !disabled && term.state === TermState.AWAITING_REVIEW,
    approveWarning: termApproveWarning(term),
    docId: term.document.id,
  });

  const rows: AdministrativeRow[] = [
    ...(data ? [contractRow(data)] : []),
    ...terms.map(termRow),
  ];

  const closeRejectionDialog = (): void => setOpenRejectionDialog(undefined);

  const handleDownload = (row: AdministrativeRow): void => {
    if (row.url) handleDownloadFile(row.url, row.fileName);
  };

  return (
    <Box my={5}>
      <Typography color="common.grey" fontSize={21} fontWeight={600} my={3}>
        {name}
      </Typography>
      <TableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCellHeader sx={{ padding: '20px 10px', width: '3%' }}>
                {IconAttachFileMS}
              </TableCellHeader>
              <TableCellHeader width="17%">documento</TableCellHeader>
              <TableCellHeader align="center" width="13%">
                data de entrada
              </TableCellHeader>
              <TableCellHeader align="center" width="17%">
                status
              </TableCellHeader>
              <TableCellHeader align="center" width="20%">
                motivo
              </TableCellHeader>
              <TableCellHeader align="center" width="30%">
                ações
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <StyledRow>
                <StyledCell align="center" colSpan={6}>
                  Documento pendente de envio
                </StyledCell>
              </StyledRow>
            )}
            {rows.map((row) => (
              <StyledRow key={row.key}>
                <StyledCell sx={{ padding: '20px 10px' }}>
                  <DownloadButton
                    disabled={!row.url}
                    onClick={() => handleDownload(row)}
                  >
                    {IconDescriptionMS}
                  </DownloadButton>
                </StyledCell>
                <StyledCell>
                  <StyledTooltip title={row.label} placement="top">
                    <Box>
                      <DocumentNameButton
                        disabled={!row.url}
                        onClick={() => handleDownload(row)}
                      >
                        <span>{row.fileName}</span>
                      </DocumentNameButton>
                    </Box>
                  </StyledTooltip>
                </StyledCell>
                <StyledCell align="center">
                  {formatOnlyDate(row.sentAt ?? undefined)}
                </StyledCell>
                <StatusCell align="center" sx={{ color: row.statusColor }}>
                  {row.statusLabel}
                </StatusCell>
                <StyledCell align="center">
                  <ActionButton
                    sx={{ textAlign: 'center' }}
                    onClick={() => setOpenRejectionDialog(row)}
                  >
                    <span>{stripHtml(row.rejectionReason)}</span>
                  </ActionButton>
                </StyledCell>
                <StyledCell>
                  <ButtonsWrapper>
                    <RejectDialog
                      docId={row.docId}
                      name={row.label}
                      document={row.fileName}
                      docLoading={docLoading}
                      handleReject={handleReject}
                      disabled={!row.actionable}
                    />
                    <ApproveAdministrativeDialog
                      docId={row.docId}
                      name={row.label}
                      document={row.fileName}
                      docLoading={docLoading}
                      disabled={!row.actionable}
                      warning={row.approveWarning}
                      handleApprove={handleApprove}
                    />
                  </ButtonsWrapper>
                </StyledCell>
              </StyledRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      {openRejectionDialog && (
        <RejectReasonDialog
          open
          closeDialog={closeRejectionDialog}
          rejectionReason={openRejectionDialog.rejectionReason || ''}
          document={openRejectionDialog.label}
        />
      )}
    </Box>
  );
}
